<template>
  <v-main>
    <navigation/>
    <guide-tour config-key="upload"/>
    <v-tabs
      v-model="tab"
      background-color="transparent"
      color="basil"
      grow
    >
      <v-tab v-for="(item, i) in uploadItems" :key="i">
        {{ item.name }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <v-tab-item v-for="(item, i) in uploadItems" :key="i">
        <v-card
          color="basil"
          flat
        >
          <v-container style="max-width: 800px; width: 80%">
            <v-row>
              <v-col v-if="doctorUser" cols="12">
                <v-select
                  v-model="selectClinic"
                  :items="clinicItems"
                  item-value="clinic_code"
                  item-text="clinic_name"
                  hide-details
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="file"
                  show-size
                  :label="item.inputLabel"
                  :disabled="isLoading"
                  prepend-icon="mdi-folder-upload"
                  :accept="item.accept"
                ></v-file-input>
              </v-col>
              <v-col class="text-center">
                <v-btn @click="handleFileUpload(item.api)" :loading="isLoading" :disabled="doctorUser ? (!selectClinic || !file) : !file">
                  {{ $t('Upload.upload') }}</v-btn>
              </v-col>
            </v-row>
            <section v-if="item.api === 'angel' && visibleAngelSummary" class="angel-upload-result" aria-label="小天使上傳結果">
              <div class="angel-upload-actions">
                <h2 class="subtitle-1 font-weight-bold">最後一次上傳</h2>
                <v-btn small outlined @click="copyAngelSummary">複製上傳資訊</v-btn>
              </div>
              <p class="angel-upload-summary" tabindex="0" aria-live="polite">{{ angelSummaryText }}</p>
              <p v-if="copyFeedback" class="text-body-2 mb-0" role="status">{{ copyFeedback }}</p>
            </section>
          </v-container>
        </v-card>
      </v-tab-item>
    </v-tabs-items>
  </v-main>
</template>

<script>
import { mapGetters } from "vuex";
import { getClinicListAPI, uploadAngelAPI, uploadDolphinAPI, uploadFiadigitAPI, uploadDrcooperAPI, uploadDentallAPI } from "@/api";
import Navigation from "../components/Navigation";
import GuideTour from "../components/utils/GuideTour.vue";
import { accountScope, beginAttempt, finishAttempt, saveAttempt, restoreAttempt, summaryText, copySummary } from "@/utils/angelUploadTrace";

export default {
  name: "Upload",
  components: {GuideTour, Navigation},
  data() {
    return {
      file: null,
      isLoading: false,
      tab: null,
      uploadItems: [
        {name: '小天使專區', inputLabel: 'Excel 檔案', accept: '.xlsx, .xls', api: 'angel'},
        {name: 'Dolphin、牙谷專區', inputLabel: 'csv 檔案', accept: '.csv', api: 'dolphin'},
        {name: '優仕專區', inputLabel: 'Excel 檔案', accept: '.xlsx, .xls, .csv', api: 'fiadigit'},
        {name: '北昕專區', inputLabel: 'csv 檔案', accept: '.csv', api: 'drcooper'},
        {name: '牙醫通專區', inputLabel: 'Excel 檔案', accept: '.xlsx, .xls', api: 'dentall'},
      ],
      clinicItems: [{clinic_code: null, clinic_name: this.$t('Upload.chooseClinic')}],
      selectClinic: null,
      angelSummary: null,
      copyFeedback: '',
    }
  },
  computed: {
    angelScope() {
      return accountScope(this.axiosParamData, this.selectClinic)
    },
    visibleAngelSummary() {
      return this.angelSummary && this.angelSummary.scope === this.angelScope ? this.angelSummary : null
    },
    angelSummaryText() {
      return summaryText(this.visibleAngelSummary)
    },
    ...mapGetters([
      'axiosParamData',
      'doctorUser'
    ]),
    mapFunc() {
      return {
        angel: uploadAngelAPI,
        dolphin: uploadDolphinAPI,
        fiadigit: uploadFiadigitAPI,
        drcooper: uploadDrcooperAPI,
        dentall: uploadDentallAPI
      }
    }
  },
  watch: {
    angelScope() {
      this.restoreAngelSummary()
      this.copyFeedback = ''
    }
  },
  methods: {
    restoreAngelSummary() {
      // Doctor pages start without a selected clinic; defer reading until selection.
      this.angelSummary = this.doctorUser && !this.selectClinic ? null : restoreAttempt(this.angelScope)
    },
    async copyAngelSummary() {
      const scope = this.angelScope
      const copied = await copySummary(this.angelSummaryText)
      if (scope === this.angelScope) this.copyFeedback = copied ? '已複製上傳資訊' : '無法自動複製，請選取上方文字手動複製'
    },
    completeAngelAttempt(attempt, response, error) {
      if (attempt.scope !== this.angelScope) return
      this.angelSummary = finishAttempt(attempt, response, error)
      saveAttempt(this.angelSummary)
      this.$notify[error ? 'error' : 'success'](this.angelSummary.message)
    },
    handleFileUpload(apiPath) {
      if (this.file) {
        this.isLoading = true
        const data = {
          ...this.axiosParamData,
          file: this.file,
        }
        if (this.doctorUser) data.clinic_code = this.selectClinic
        const attempt = apiPath === 'angel' ? beginAttempt(this.angelScope, this.file) : null
        if (attempt) {
          data.upload_id = attempt.uploadId
          this.angelSummary = attempt
          this.copyFeedback = ''
          saveAttempt(attempt)
        }
        this.mapFunc[apiPath](data).then((res) => {
          if (attempt) {
            this.completeAngelAttempt(attempt, res, null)
          } else if (res.data.count) {
            this.$notify.success(`已成功上傳${res.data.count}筆約診資料，如有數量落差請重新匯出上傳，同步時間為10分鐘請稍等`)
          } else {
            this.$notify.success(this.$t('Upload.success'))
          }
        }).catch(e => {
          if (attempt) {
            this.completeAngelAttempt(attempt, null, e)
          } else if (e.response && e.response.data && e.response.data.messages) {
            this.$notify.error(e.response.data.messages)
          } else {
            this.$notify.error(this.$t('Upload.fileLargeError'))
          }
        }).finally(() => {
          this.isLoading = false
        })
        this.file = null
      } else {
        this.$notify.warning(this.$t('Upload.chooseFile'))
      }
    },
  },
  mounted() {
    this.restoreAngelSummary()
    if (this.doctorUser) {
      getClinicListAPI(this.axiosParamData).then(res => {
        this.clinicItems.push(...res.data)
      })
    }
  }
}
</script>

<style scoped>
.angel-upload-result {
  margin-top: 24px;
  padding: 16px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  min-width: 0;
}
.angel-upload-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.angel-upload-summary {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  user-select: text;
  line-height: 1.7;
  margin-bottom: 0;
}
@media (max-width: 480px) {
  .angel-upload-result { padding: 12px; }
  .angel-upload-actions { flex-direction: column; align-items: stretch; }
}
</style>
